import { useEffect, useState, useRef, useCallback } from 'react';
import { useComlinkWorker, createComlinkWorkerFactory } from "hooks/useComlinkWorker";
import {
  setTensorflowWorker,
  setRecreateTensorflowWorker,
  loadModelsIntoWorker,
  areModelsReady,
} from "services/models";
import { WORKER_CONFIG } from '../constants/worker';
import { sleep } from '../utils/workerHelpers';
import type { WorkerApiInterface } from 'types/models';

const workerFactory = createComlinkWorkerFactory(() => import("../workers/tensorflow.worker.ts?worker"));

/**
 * Hook for loading and managing TensorFlow models in a web worker
 */
const useModels = () => {
  const [isDoneLoadingModels, setIsDoneLoadingModels] = useState(false);
  const hasStartedLoadingRef = useRef(false);
  const isLoadingModelsRef = useRef(false);
  const lastWorkerRef = useRef<unknown>(null);
  /**
   * When recovery recreates the worker it also owns model loading. Skip the
   * auto-load that would otherwise race on the same worker and leave the
   * camera competing with a second download+warmup (very slow frame processing).
   */
  const skipNextAutoLoadRef = useRef(false);
  const { worker, recreateWorker } = useComlinkWorker<WorkerApiInterface>(workerFactory);

  // Set worker in services/models when available; null it out on unmount
  useEffect(() => {
    if (worker) {
      setTensorflowWorker(worker);
    }
    return () => {
      // [M1-FIX] Null the worker reference on unmount so models.ts doesn't
      // hold a stale reference to a terminated Comlink proxy
      setTensorflowWorker(null);
    };
  }, [worker]);

  // Allow services/models recovery to terminate+recreate a hung worker
  useEffect(() => {
    setRecreateTensorflowWorker(async () => {
      skipNextAutoLoadRef.current = true;
      const nextWorker = await recreateWorker();
      setTensorflowWorker(nextWorker);
      return nextWorker !== null;
    });
    return () => {
      setRecreateTensorflowWorker(null);
    };
  }, [recreateWorker]);

  /**
   * Load models into the worker via the shared service routine
   */
  const loadModels = useCallback(async () => {
    if (!worker) {
      console.warn('Models: Worker not ready');
      return;
    }
    if (isLoadingModelsRef.current || isDoneLoadingModels) {
      return;
    }

    // Recovery (or a prior load on this worker) may already have models ready
    try {
      if (await areModelsReady()) {
        setIsDoneLoadingModels(true);
        return;
      }
    } catch {
      // proceed to load
    }

    isLoadingModelsRef.current = true;

    try {
      const result = await loadModelsIntoWorker("initial");
      if (result.success) {
        setIsDoneLoadingModels(true);
      }
    } finally {
      isLoadingModelsRef.current = false;
    }
  }, [worker, isDoneLoadingModels]);

  useEffect(() => {
    if (worker && worker !== lastWorkerRef.current) {
      lastWorkerRef.current = worker;

      if (skipNextAutoLoadRef.current) {
        // Recovery will call loadModelsIntoWorker — do not start a competing load
        skipNextAutoLoadRef.current = false;
        hasStartedLoadingRef.current = true;
        isLoadingModelsRef.current = false;
        return;
      }

      hasStartedLoadingRef.current = false;
      isLoadingModelsRef.current = false;
    }
    if (worker && !hasStartedLoadingRef.current) {
      hasStartedLoadingRef.current = true;
      // Small delay to ensure worker is fully initialized
      sleep(WORKER_CONFIG.INITIALIZATION_DELAY).then(() => {
        loadModels();
      });
    }
  }, [worker, loadModels]);

  return {
    isDoneLoadingModels,
  };
};

export default useModels;
